import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Ticket } from 'lucide-react';
import { useBetMode, legFor } from '../lib/betMode';
import { InjuryTag } from './InjuryTag';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting`;
const pct = (p) => `${Math.round(p * 100)}%`;
const time = (d) => (d ? new Date(d).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' }) : '');

const Row = ({ x, n }) => {
  const mode = useBetMode();
  return (
  <li className="border-t border-zinc-800/70 pt-1.5 first:border-0 first:pt-0" data-testid={`open-prop-${n}`}>
    <div className="flex items-baseline gap-2">
      <span className="w-5 text-[10px] font-mono text-zinc-500">{n}.</span>
      <span className="flex-1 min-w-0">
        <span className="text-[13px] font-semibold text-white">{legFor(mode, x)}</span>
        {x.injury && <span className="ml-1"><InjuryTag injury={x.injury} testId={`open-prop-${n}-injury`} withDetail /></span>}
        <span className="block text-[10px] text-zinc-500 truncate">{x.depth ? `${x.depth} · ` : ''}{x.game} · {time(x.date)} PT · expects {x.projection}</span>
      </span>
      <span className="font-mono text-xs font-bold text-emerald-300">{pct(x.prob)}</span>
    </div>
    <p className={`ml-7 text-[10px] ${x.lock ? 'text-emerald-300' : 'text-amber-300'}`}>{x.lock ? 'Passed every safety check' : `Caution: ${x.lock_notes.join(', ')}`}</p>
  </li>
  );
};

const PropTicket = ({ legs, size, league }) => {
  const mode = useBetMode();
  if (!legs.length) return null;
  const chance = legs.reduce((a, x) => a * x.prob, 1);
  const track = async () => {
    const body = {
      leg_count: legs.length, descriptor: `${league.toUpperCase()} props ${legs.length}-leg (not on my tickets)`,
      legs: legs.map((x, i) => ({ id: `p${i}`, league, bet_type: 'player_prop', player_name: x.player, stat_target: x.stat,
        line: `${x.mark}+`, selection: `${x.player} ${x.mark}+ ${x.label.toLowerCase()}` })),
    };
    try { await axios.post(`${API}/parlays`, body); toast.success(`${legs.length}-leg props ticket added to your tracker`); } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  return (
    <div className="min-w-0 rounded-lg border border-violet-500/30 bg-zinc-950/60 p-3" data-testid={`open-props-ticket-${size}`}>
      <div className="flex items-center gap-2">
        <p className="flex-1 text-sm font-bold text-white">{legs.length}-leg props ticket</p>
        <span className="font-mono text-xs font-bold text-violet-300">{pct(chance)} all hit</span>
        <button type="button" onClick={track} data-testid={`open-props-track-${size}`}
          className="inline-flex items-center gap-1 rounded bg-amber-500 px-2 py-0.5 text-[11px] font-bold text-black"><Ticket className="w-3 h-3" />Track</button>
      </div>
      <ul className="mt-1 space-y-0.5 text-[11px] text-zinc-300 break-words">
        {legs.map((x) => <li key={`${x.player}-${x.stat}`}>{x.lock ? '✅' : '⚠️'} {legFor(mode, x)} <span className="text-zinc-500">({pct(x.prob)})</span></li>)}
      </ul>
    </div>
  );
};

// Passing / rushing / receiving picks for players NOT already on an active ticket (shown inside PicksNotOnTickets).
export function OpenPropsBody({ league = 'nfl' }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    axios.get(`${API}/board/open-props`, { params: { league } }).then((r) => setD(r.data)).catch((e) => setErr(e.response?.data?.detail || e.message));
  }, [league]);
  return (
    <div className="space-y-3 min-w-0" data-testid="open-props-body">
      <p className="text-[11px] text-zinc-500">{league === 'nba' ? 'Points, rebounds, assists and 3-pointer' : 'Passing, rushing and receiving'} picks for players who are <b>not</b> on your open tickets (their game can be).</p>
          {!d && !err && <p className="flex items-center gap-2 text-sm text-zinc-400 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Reading your tickets…</p>}
          {err && <p className="text-sm text-red-400" data-testid="open-props-error">{err}</p>}
          {d && (
            <div className="space-y-3 min-w-0">
              <p className="text-[11px] text-zinc-400" data-testid="open-props-summary">
                Checked <b className="text-white">{d.tickets_checked}</b> ticket{d.tickets_checked === 1 ? '' : 's'} · skipped <b className="text-white">{d.skipped_players.length}</b> players you have · <b className="text-white">{d.picks.length}</b> open picks
              </p>
              {d.picks.length === 0 ? <p className="text-sm text-zinc-400" data-testid="open-props-empty">No open player picks left that aren't on your tickets.</p> : (
                <>
                  <div className="grid gap-2"><PropTicket legs={d.ticket6} size={6} league={league} />{d.ticket8?.length > (d.ticket6?.length || 0) && <PropTicket legs={d.ticket8} size={8} league={league} />}</div>
                  <p className="text-xs font-semibold text-zinc-300">All open picks, safest first</p>
                  <ol className="space-y-1.5" data-testid="open-props-list">{d.picks.map((x, i) => <Row key={`${x.player}-${x.stat}`} x={x} n={i + 1} />)}</ol>
                </>
              )}
              {d.skipped_players.length > 0 && <p className="text-[11px] text-zinc-500" data-testid="open-props-skipped">Already on your tickets: {d.skipped_players.join(', ')}</p>}
            </div>
          )}
    </div>
  );
}
